import React, { useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiSave } from 'react-icons/fi';


const NotificationSettings = () => {
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState({
    notifications: {
      emailCampaigns: settings.notifications?.emailCampaigns ?? true,
      callResults: settings.notifications?.callResults ?? true,
      systemUpdates: settings.notifications?.systemUpdates ?? false
    },
    timezone: settings.timezone || 'UTC'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings(formData);
    toast.success('Notification settings saved successfully');
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [name]: checked
      }
    }));
  };

  const handleTimezoneChange = (e) => {
    setFormData(prev => ({
      ...prev,
      timezone: e.target.value
    }));
  };

  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)' }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Notification Preferences</h3>
        <p className="text-gray-600">
          Configure when and how you want to receive notifications.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Email Notifications</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <label htmlFor="emailCampaigns" className="text-sm font-medium text-gray-900">
                  Email Campaign Updates
                </label>
                <p className="text-sm text-gray-500">
                  Get notified when email campaigns are sent, opened, or clicked
                </p>
              </div>
              <input
                type="checkbox"
                id="emailCampaigns"
                name="emailCampaigns"
                checked={formData.notifications.emailCampaigns}
                onChange={handleNotificationChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <label htmlFor="callResults" className="text-sm font-medium text-gray-900">
                  AI Call Results
                </label>
                <p className="text-sm text-gray-500">
                  Receive notifications when AI calls are completed with results
                </p>
              </div>
              <input
                type="checkbox"
                id="callResults"
                name="callResults"
                checked={formData.notifications.callResults}
                onChange={handleNotificationChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div>
                <label htmlFor="systemUpdates" className="text-sm font-medium text-gray-900">
                  System Updates
                </label>
                <p className="text-sm text-gray-500">
                  Get notified about new features and system maintenance
                </p>
              </div>
              <input
                type="checkbox"
                id="systemUpdates"
                name="systemUpdates"
                checked={formData.notifications.systemUpdates}
                onChange={handleNotificationChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            value={formData.timezone}
            onChange={handleTimezoneChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-gray-500">
            Used for scheduling campaigns and displaying timestamps.
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Notification Delivery</h4>
          <p className="text-sm text-blue-700">
            Notifications will be sent to your registered email address. 
            You can also view all notifications in the dashboard.
          </p>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationSettings;